import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { BRAND } from "../styles/authTheme";

const STEPS = [
  { num: 1, label: "Face Auto-Detect", shortLabel: "Detect", Icon: PhotoCameraOutlinedIcon },
  { num: 2, label: "Punch", shortLabel: "Punch", Icon: TouchAppOutlinedIcon },
];

const KioskStepBar = ({ activeStep = 1, compact = false }) => {
  const current = STEPS.find((s) => s.num === activeStep) || STEPS[0];

  return (
    <Box
      sx={{
        mb: compact ? { xs: 1, sm: 1.5 } : { xs: 1.5, sm: 2.5 },
        borderRadius: "14px",
        bgcolor: "#F1F5F9",
        border: `1px solid ${BRAND.border}`,
        px: { xs: 1.25, sm: 2 },
        py: { xs: 1.1, sm: 1.5 },
      }}
    >
      {/* Segmented progress track */}
      <Box sx={{ display: "flex", gap: 0.75, mb: { xs: 1.1, sm: 1.35 } }}>
        {STEPS.map((step) => {
          const isDone = step.num < activeStep;
          const isActive = step.num === activeStep;

          return (
            <Box
              key={step.num}
              sx={{
                flex: 1,
                height: { xs: 5, sm: 6 },
                borderRadius: "999px",
                bgcolor: isDone
                  ? BRAND.success
                  : isActive
                  ? BRAND.orange
                  : "#E2E8F0",
                boxShadow: isActive ? "0 2px 8px rgba(230,90,0,0.35)" : "none",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
              }}
            />
          );
        })}
      </Box>

      {/* Step labels */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
        {STEPS.map((step) => {
          const isDone = step.num < activeStep;
          const isActive = step.num === activeStep;
          const StepIcon = step.Icon;
          const color = isDone ? BRAND.success : isActive ? BRAND.orange : BRAND.muted;

          return (
            <Box
              key={step.num}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.35,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDone
                    ? "rgba(22, 163, 74, 0.12)"
                    : isActive
                    ? BRAND.orangeLight
                    : "#fff",
                  border: `2px solid ${
                    isDone ? BRAND.success : isActive ? BRAND.orange : "#E2E8F0"
                  }`,
                  transition: "all 0.25s ease",
                }}
              >
                {isDone ? (
                  <CheckIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: BRAND.success }} />
                ) : (
                  <StepIcon
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      color: isActive ? BRAND.orange : BRAND.muted,
                    }}
                  />
                )}
              </Box>

              <Typography
                noWrap
                sx={{
                  fontWeight: isActive ? 700 : isDone ? 600 : 500,
                  color,
                  fontSize: { xs: "0.65rem", sm: "0.72rem" },
                  lineHeight: 1.2,
                  textAlign: "center",
                  maxWidth: "100%",
                }}
              >
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                  {step.shortLabel}
                </Box>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {step.label}
                </Box>
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Current step caption — mobile friendly */}
      <Typography
        sx={{
          mt: { xs: 0.85, sm: 1 },
          textAlign: "center",
          fontSize: { xs: "0.72rem", sm: "0.78rem" },
          color: BRAND.muted,
          fontWeight: 500,
        }}
      >
        Step {activeStep} of {STEPS.length}
        <Box component="span" sx={{ color: BRAND.navy, fontWeight: 700 }}>
          {" "}
          · {current.label}
        </Box>
      </Typography>
    </Box>
  );
};

export function LiveClock({ variant = "dark", compact = false, inline = false, timeOnly = false }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const date = now.toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  const isLight = variant === "light";

  if (inline) {
    return (
      <Box sx={{ textAlign: "right", lineHeight: 1, flexShrink: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            color: isLight ? BRAND.navy : "rgba(255,255,255,0.95)",
            fontWeight: 700,
            fontSize: timeOnly ? "0.7rem" : "0.72rem",
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
          }}
        >
          {time}
        </Typography>
        {!timeOnly && (
          <Typography
            component="span"
            sx={{
              display: "block",
              color: isLight ? BRAND.muted : "rgba(255,255,255,0.65)",
              fontSize: "0.62rem",
              whiteSpace: "nowrap",
            }}
          >
            {date}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: compact ? "left" : "right" }}>
      <Typography
        variant="body2"
        sx={{
          color: isLight ? BRAND.navy : "rgba(255,255,255,0.95)",
          fontWeight: 700,
          lineHeight: 1.2,
          fontSize: compact ? "0.85rem" : "0.95rem",
        }}
      >
        {time}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: isLight ? BRAND.muted : "rgba(255,255,255,0.65)" }}
      >
        {date}
      </Typography>
    </Box>
  );
}

export default KioskStepBar;
